/**
 * PathEnumGlyph — clickable inline enum-state glyph for a JSON-path row.
 *
 * Rendered next to a property name inside the JSON Peek popover and the
 * JSON structure details dialog. Two states:
 *
 *   • `declared`  — the path has an `EnumColumnDeclaration` in MetaInfo.
 *                   Blue `▾` glyph; clicking opens the enum-values details
 *                   dialog scoped to this path.
 *   • `eligible`  — the path's leaf name passes `nameSuggestsEnum` (e.g.
 *                   `status`, `priority`, `type`) but isn't declared yet.
 *                   Gray `▾?` glyph; clicking opens the declare dialog
 *                   pre-filled with the path + pathKind.
 *
 * The two-state model mirrors `EnumHeaderGlyph` (the column-header trio
 * `declared` / `native` / `eligible`) but drops `native` — engine-level
 * native JSON enums don't exist for paths inside a JSON document.
 */
import React from 'react';

import {
  GLYPH_DECLARED_CLASS,
  GLYPH_MUTED_CLASS,
} from '../grid-glyphs/gridGlyphStyles';

export type PathEnumGlyphState = 'declared' | 'eligible';

type PathEnumGlyphProps = {
  state: PathEnumGlyphState;
  /** JSONPath; used to build a unique testid (`path-enum-glyph-<state>-<path>`). */
  path: string;
  onClick: () => void;
};

const LABEL: Record<PathEnumGlyphState, string> = {
  declared: '▾',
  eligible: '▾?',
};

const TITLE: Record<PathEnumGlyphState, string> = {
  declared: 'Declared as enum — click for details',
  eligible: 'Looks like an enum — click to declare',
};

const CLASS_FOR: Record<PathEnumGlyphState, string> = {
  declared: GLYPH_DECLARED_CLASS,
  eligible: GLYPH_MUTED_CLASS,
};

export const PathEnumGlyph = React.memo(function PathEnumGlyph({
  state,
  path,
  onClick,
}: PathEnumGlyphProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`${CLASS_FOR[state]} ml-1 px-0.5 shrink-0`}
      title={TITLE[state]}
      aria-label={TITLE[state]}
      data-testid={`path-enum-glyph-${state}-${path}`}
    >
      {LABEL[state]}
    </button>
  );
});
